/**
 * ProductPickerDialog – lightweight product picker for adding more items
 * to a multi-item preorder. Shows available products from the same producer.
 *
 * Product images are resolved via media_assets (single source of truth).
 */
import { useState, useEffect, useMemo } from 'react';
import { Plus, Package } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { ProductThumb } from '@/components/shop/ProductThumb';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import type { PreorderProduct } from './PickupOrderDialog';

interface ProductPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  producerId: string;
  producerName: string;
  existingProductIds: string[];
  onSelect: (product: PreorderProduct) => void;
}

interface RawProduct {
  id: string;
  title: string;
  price_ore: number;
  currency: string;
  pickup_instructions: string | null;
  stock_limit: number | null;
}

function formatPrice(amount: number, currency: string = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ProductPickerDialog({
  open,
  onOpenChange,
  producerId,
  producerName,
  existingProductIds,
  onSelect,
}: ProductPickerDialogProps) {
  const [products, setProducts] = useState<RawProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [remainingMap, setRemainingMap] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);

    async function fetchProducts() {
      const { data, error } = await supabase
        .from('producer_products')
        .select('id, title, price_ore, currency, pickup_instructions, stock_limit')
        .eq('producer_id', producerId)
        .eq('status', 'public')
        .is('deleted_at', null)
        .order('title');

      if (!error && data) {
        setProducts(data as RawProduct[]);

        // Fetch remaining stock for products with stock_limit
        const limitedIds = (data as RawProduct[])
          .filter(p => p.stock_limit !== null)
          .map(p => p.id);

        if (limitedIds.length > 0) {
          const remainingResults: Record<string, number | null> = {};
          // Use individual RPC calls for each product
          await Promise.all(
            limitedIds.map(async (id) => {
              const { data: remaining } = await supabase.rpc('get_product_remaining', {
                p_product_id: id,
              });
              remainingResults[id] = remaining as number | null;
            })
          );
          setRemainingMap(remainingResults);
        }
      }
      setLoading(false);
    }

    fetchProducts();
  }, [open, producerId]);

  const availableProducts = products.filter(p => !existingProductIds.includes(p.id));

  // Batch cover images from media_assets (single source of truth)
  const allProductIds = useMemo(() => availableProducts.map(p => p.id), [availableProducts]);
  const { data: coverMap } = useProductCoverImages(allProductIds);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Lägg till vara</DialogTitle>
          <DialogDescription className="text-sm">
            Välj en produkt från {producerName}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Laddar…</div>
        ) : availableProducts.length === 0 ? (
          <div className="py-8 text-center space-y-2">
            <Package className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">
              {products.length === existingProductIds.length
                ? 'Alla produkter är redan tillagda.'
                : 'Inga fler produkter att lägga till.'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {availableProducts.map((p) => {
              const thumbSrc = coverMap?.[p.id] ?? null;
              const remaining = remainingMap[p.id] ?? (p.stock_limit !== null ? p.stock_limit : null);
              const isOutOfStock = remaining !== null && remaining <= 0;

              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={isOutOfStock}
                  onClick={() =>
                    onSelect({
                      id: p.id,
                      title: p.title,
                      price: p.price_ore / 100,
                      currencyCode: p.currency || 'SEK',
                      imageUrl: thumbSrc,
                      pickupInstructions: p.pickup_instructions,
                      stockLimit: p.stock_limit,
                      remaining,
                    })
                  }
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/70 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ProductThumb
                    src={thumbSrc}
                    title={p.title}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{p.title}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatPrice(p.price_ore / 100, p.currency || 'SEK')}
                      </span>
                      {isOutOfStock && (
                        <span className="text-xs text-destructive">Slut</span>
                      )}
                      {remaining !== null && remaining > 0 && (
                        <span className="text-xs text-muted-foreground">{remaining} kvar</span>
                      )}
                    </div>
                  </div>
                  {!isOutOfStock && (
                    <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
